import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AlertDialog as Base } from '@base-ui/react/alert-dialog';
import { CANVAS_UI } from '../features/editor/canvas-ui';
import { Button } from './Button';
import { returnFocusUnlessMoved } from './focus';

/**
 * A question the person must answer before something irreversible (or surprising) happens: a Base UI alert dialog
 * (`role="alertdialog"`) dressed like `Dialog` (bg-chrome, shadow-float). The caller owns `open` and closes it in both
 * handlers: `onConfirm` after the action (it may return a promise: the buttons wait, the Confirm one shows a spinner),
 * `onCancel` for the Cancel button, Escape and a press on the backdrop. Cancel is always the safe way out.
 *
 * `destructive`: the focus starts on Cancel, so a stray Enter or Space never deletes; otherwise it starts on Confirm.
 * Focus goes back to the element that had it when the dialog opened (its trigger), unless the person moved it since.
 * Strings are props (title, body, labels): this primitive holds no copy, so FR and EN come from the caller.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  body?: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel: ReactNode;
  destructive?: boolean;
  onConfirm: () => void | Promise<unknown>;
  onCancel: () => void;
}) {
  const popup = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const confirm = useRef<HTMLButtonElement>(null);
  const opener = useRef<Element | null>(null);
  const [busy, setBusy] = useState(false);
  // Before the popup takes the focus: what had it is the trigger to give it back to.
  useLayoutEffect(() => {
    if (open) opener.current = document.activeElement;
  }, [open]);

  const accept = () => {
    const result = onConfirm();
    if (!result || typeof (result as Promise<unknown>).then !== 'function') return;
    setBusy(true);
    void Promise.resolve(result).finally(() => setBusy(false));
  };
  const refuse = () => {
    if (!busy) onCancel();
  };

  return (
    <Base.Root open={open} onOpenChange={(next) => !next && refuse()}>
      <Base.Portal>
        {/* An alert dialog ignores outside presses by default; here a press on the backdrop is a Cancel. */}
        <Base.Backdrop data-backdrop="" className="os-fade fixed inset-0 z-(--layer-dialog) bg-overlay" onPointerDown={refuse} />
        <Base.Popup
          ref={popup}
          initialFocus={destructive ? cancel : confirm}
          finalFocus={() => returnFocusUnlessMoved(popup, opener.current)()}
          data-destructive={destructive || undefined}
          {...{ [CANVAS_UI]: '' }}
          className="os-pop fixed left-1/2 top-1/2 z-(--layer-dialog) max-h-[85dvh] w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-3xl bg-chrome p-6 font-sans text-sm text-fg shadow-float outline-none"
        >
          <Base.Title className="mb-2 font-display text-title font-semibold text-fg">{title}</Base.Title>
          {body && <Base.Description className="text-sm text-fg-muted">{body}</Base.Description>}
          <div className="mt-5 flex justify-end gap-2">
            <Button ref={cancel} onClick={refuse}>
              {cancelLabel}
            </Button>
            <Button ref={confirm} variant="primary" loading={busy} onClick={accept}>
              {confirmLabel}
            </Button>
          </div>
        </Base.Popup>
      </Base.Portal>
    </Base.Root>
  );
}
